import {
  Button,
  formatDate,
  formatPhone,
  ListView,
  PageHeader,
  type SearchResult,
  SearchSelect,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { businessActions } from "../../businesses";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import type { AcquisitionSource } from "../../labels";
import { filterSearch } from "../../list-search";
import { useDebounced } from "../../search";
import { meQuery } from "../../session";
import styles from "../customers/customers.module.css";
import { type Acquisition, AcquisitionFields } from "../shared/acquisition-fields";

export const businessFilters = filterSearch({ q: "text" });

/** Businesses (P8-11): business accounts, their branches, members and credit. */
export function BusinessesPage(): ReactNode {
  const search = useSearch({ from: "/console/businesses" });
  const navigate = useNavigate({ from: "/businesses" });
  const format = useCompanyFormat();
  const { data: me } = useSuspenseQuery(meQuery);
  const canManage = me?.permissions.includes("customers.manage") ?? false;
  const [typed, setTyped] = useState(search.q ?? "");
  const q = useDebounced(typed.trim());
  useEffect(() => {
    if ((search.q ?? "") !== q) void navigate({ search: { q: q || undefined }, replace: true });
  }, [q, search.q, navigate]);
  const [adding, setAdding] = useState(false);
  const list = useCursorList({
    queryKey: ["businesses", search.q ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/businesses", {
        params: { query: { q: search.q, cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });

  return (
    <>
      <PageHeader
        title="Businesses"
        description="Business accounts, their members and credit."
        actions={canManage ? <Button onClick={() => setAdding(true)}>Add business</Button> : undefined}
      />
      <ListView
        caption="Businesses"
        itemName={{ one: "business", other: "businesses" }}
        rows={list.rows}
        rowKey={(b) => b.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(search.q)}
        onResetFilters={() => setTyped("")}
        empty={{
          title: "No businesses yet",
          description: "Business accounts order for hotels, offices and estates, often on credit.",
        }}
        toolbar={
          <div className={styles.searchBox}>
            <SearchField label="Search businesses" value={typed} onValueChange={setTyped} placeholder="Business name" />
          </div>
        }
        columns={[
          {
            key: "business_name",
            label: "Business",
            render: (b) => (
              <span className={styles.stack}>
                <Link to="/businesses/$businessId" params={{ businessId: b.id }} className={styles.link}>
                  {b.business_name}
                </Link>
                {b.parent ? <span className={styles.muted}>Branch of {b.parent.business_name}</span> : null}
              </span>
            ),
          },
          { key: "phone", label: "Phone", render: (b) => formatPhone(b.phone) || "—" },
          { key: "members", label: "Members", render: (b) => String(b.members.length) },
          {
            key: "is_active",
            label: "Status",
            render: (b) => (
              <Badge tone={b.is_active ? "success" : "neutral"}>{b.is_active ? "Active" : "Inactive"}</Badge>
            ),
          },
          { key: "created_at", label: "Added", render: (b) => formatDate(b.created_at, format) },
        ]}
      />
      {adding ? <BusinessDialog onClose={() => setAdding(false)} /> : null}
    </>
  );
}

/** Add a business, or edit one's details. */
export function BusinessDialog({
  business,
  onClose,
}: {
  business?: {
    id: string;
    business_name: string;
    tax_pin: string | null;
    email: string | null;
    phone: string | null;
    parent: { id: string; business_name: string } | null;
    acquisition_source: AcquisitionSource | null;
    account_manager_staff_id: string | null;
  };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(business?.business_name ?? "");
  const [pin, setPin] = useState(business?.tax_pin ?? "");
  const [email, setEmail] = useState(business?.email ?? "");
  const [phone, setPhone] = useState(business?.phone ?? "");
  // a branch belongs to a parent business (an estate, a chain); none is a business on its own
  const [parent, setParent] = useState<SearchResult | null>(
    business?.parent ? { id: business.parent.id, label: business.parent.business_name } : null,
  );
  const [won, setWon] = useState<Acquisition>({
    source: business?.acquisition_source ?? null,
    staffId: business?.account_manager_staff_id ?? null,
  });
  const mutation = useMutation({
    mutationFn: () =>
      business
        ? // a field emptied on an edit is removed (null); undefined would keep the old value
          businessActions.update(business.id, {
            business_name: name.trim(),
            tax_pin: pin.trim() || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            parent_id: parent?.id ?? null,
            // a source can be changed but not cleared; the manager can be (null)
            acquisition_source: won.source ?? undefined,
            account_manager_staff_id: won.staffId,
          })
        : businessActions.create({
            business_name: name.trim(),
            tax_pin: pin.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            parent_id: parent?.id,
            acquisition_source: won.source ?? undefined,
            account_manager_staff_id: won.staffId ?? undefined,
          }),
    onSuccess: (saved) => {
      onClose();
      if (!business) void navigate({ to: "/businesses/$businessId", params: { businessId: saved.id } });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={business ? `Edit ${business.business_name}` : "Add business"}>
        <div className={styles.form}>
          <Input label="Business name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Tax PIN (optional)"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            description="On invoices, and for eTIMS."
          />
          <div className={styles.pair}>
            <Input label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input
              label="Phone (optional)"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+254712345678"
            />
          </div>
          <ParentPicker value={parent} onChange={setParent} exclude={business?.id} />
          <AcquisitionFields
            value={won}
            onChange={setWon}
            staffLabel="Account manager"
            staffHelp="Looks after the account. Its orders count as their sales, unless staff place an order themselves."
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {business ? "Save" : "Add business"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The business this one is a branch of, found by search; empty for a business on its own. */
function ParentPicker({
  value,
  onChange,
  exclude,
}: {
  value: SearchResult | null;
  onChange: (parent: SearchResult | null) => void;
  exclude?: string;
}) {
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim());
  const found = useQuery({
    queryKey: ["business-search", q],
    enabled: q.length >= 2,
    queryFn: async (): Promise<SearchResult[]> =>
      (await ok(api.GET("/v1/admin/businesses", { params: { query: { q, limit: 8 } } }))).data
        .filter((b) => b.id !== exclude)
        .map((b) => ({ id: b.id, label: b.business_name })),
  });
  return (
    <SearchSelect
      label="Branch of (optional)"
      placeholder="Search for the parent business"
      value={value}
      onChange={onChange}
      query={query}
      onQueryChange={setQuery}
      results={found.data ?? []}
      pending={query.trim() !== q || found.isFetching}
      description="An estate's or chain's branch. Leave it empty for a business on its own."
    />
  );
}
